import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import ServerError from '../../../../../../components/ServerError';
import SkeletonCard from '../../../../components/Skeleton/Card';
import { EmptyGoals } from '../EmptyGoals';
import { GoalFilter } from '../GoalsFilter/GoalsFilter';
import { GoalsFilterConnected } from '../GoalsFilter/GoalsFilterConnected';
import { GoalBase, GoalId, GoalViewBy } from '../types';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { useObjectives } from '../useObjectives';
import { GoalsList } from './GoalsList';
import { useGoalViewBy } from '../useGoalViewBy';
import { useGoalWeightValidation } from '../useGoalWeightValidation';
import { useGoalSelection } from '../GoalBulkApproval';

type Props = {
  isReviewGoals?: boolean;
  onCreateGoal: () => void;
  onGoalClick: (goalId: GoalId) => void;
  filter: GoalFilter;
  onFilterChange: (filter: GoalFilter) => void;
};

export const GoalsListWithFilter: React.FC<Props> = ({
  isReviewGoals = false,
  filter,
  onFilterChange,
  onCreateGoal,
  onGoalClick,
}) => {
  const {
    currentPeriodId,
    employeeAssignedCategories,
    developmentFrameworkEnabled,
  } = useGoalsContext();
  const [viewBy, setViewBy] = useGoalViewBy();
  const goalWeightValidation = useGoalWeightValidation(filter);

  const [onlyInvalidWeights, setOnlyInvalidWeights] = useState(false);

  useEffect(() => {
    if (viewBy !== GoalViewBy.EMPLOYEE && onlyInvalidWeights) {
      setOnlyInvalidWeights(false);
    }
  }, [viewBy, onlyInvalidWeights]);

  const { goals, loading, error } = useObjectives(
    filter,
    isReviewGoals ? ['employee'] : 'all', // show only employee goals when we are in feedback cycle page
  );

  const { setSelectableGoals } = useGoalSelection();
  // Sync all goals to ref (no state update, no re-render loop)
  setSelectableGoals(goals ?? []);

  const hasWeightedCategories =
    viewBy === GoalViewBy.CATEGORY &&
    employeeAssignedCategories.some((a) => a.weight != null);

  if (error) {
    return <ServerError />;
  }

  const renderGoals = (goals: GoalBase[]) => {
    if (!goals.length && !hasWeightedCategories) {
      return (
        <EmptyGoals
          createBtnVisible={!!currentPeriodId || !developmentFrameworkEnabled}
          onCreateGoal={onCreateGoal}
        />
      );
    }
    return (
      <GoalsList
        goals={goals}
        onGoalClick={onGoalClick}
        viewBy={viewBy}
        goalWeightValidation={goalWeightValidation}
        onlyInvalidWeights={onlyInvalidWeights}
      />
    );
  };

  return (
    <Row>
      <Col>
        <GoalsFilterConnected
          goals={goals}
          filter={filter}
          onChange={onFilterChange}
          isReviewGoals={isReviewGoals}
          viewBy={viewBy}
          onViewByChange={setViewBy}
          goalWeightValidation={goalWeightValidation}
          onActivateInvalidOnly={() => {
            setViewBy(GoalViewBy.EMPLOYEE);
            setOnlyInvalidWeights(true);
          }}
        />
        {loading || goals === undefined ? (
          <>
            <SkeletonCard height={400} />
            <SkeletonCard height={400} />
          </>
        ) : (
          renderGoals(goals)
        )}
      </Col>
    </Row>
  );
};
